import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Check from 'lucide-react/dist/esm/icons/check';
import TriangleAlert from 'lucide-react/dist/esm/icons/triangle-alert';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import Button from '~/modules/components/global/button';

const CopyField = ({ value, 'data-testid': testId, 'data-copy-testid': copyTestId }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(value);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2.5">
			<code className="flex-1 break-all font-mono text-sm" data-testid={testId}>
				{value}
			</code>
			<button
				type="button"
				onClick={handleCopy}
				data-testid={copyTestId}
				aria-label="Copy to clipboard"
				className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none"
			>
				{copied
					? <Check className="h-4 w-4 text-green-400" />
					: <Copy className="h-4 w-4" />
				}
			</button>
		</div>
	);
};

CopyField.propTypes = {
	value: PropTypes.string.isRequired,
	'data-testid': PropTypes.string,
	'data-copy-testid': PropTypes.string
};

const WebhookSecretRevealModal = ({ secret, onClose }) => {
	return (
		<Dialog open={!!secret} onOpenChange={(open) => {
			if(!open) {
				onClose();
			}
		}}
		>
			<DialogContent
				className="rounded-xl p-0 overflow-hidden"
				style={{ maxWidth: '38rem' }}
				onInteractOutside={(e) => e.preventDefault()}
			>
				<div className="p-6 space-y-4">
					<div className="flex items-start gap-2 rounded-md border-l-2 border-l-yellow-400 bg-muted/40 px-4 py-3 text-sm">
						<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" />
						<div>
							<strong className="font-semibold">Copy this secret now.</strong> This is the only time it will
							be shown. Use it with a Standard Webhooks library to verify every delivery.
						</div>
					</div>

					<CopyField
						value={secret || ''}
						data-testid="webhook-secret-revealed-value"
						data-copy-testid="webhook-secret-copy-button"
					/>

					<p className="text-xs text-muted-foreground">
						Pass the raw request body and the <code>webhook-id</code>, <code>webhook-timestamp</code>,
						and <code>webhook-signature</code> headers to the verifier.
					</p>
				</div>

				<div className="flex justify-end border-t border-border px-6 py-4">
					<Button onClick={onClose} data-testid="webhook-secret-reveal-close-button">
						Done
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

WebhookSecretRevealModal.propTypes = {
	secret: PropTypes.string,
	onClose: PropTypes.func.isRequired
};

export default WebhookSecretRevealModal;
